import { Moon, Sun } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  applyThemeToDocument,
  readStoredTheme,
  writeStoredTheme,
  type MorningTheme,
} from '@/lib/theme'
import { cn } from '@/lib/utils'

type ThemeToggleProps = {
  className?: string
  /** Header glass: icon-only control that matches the bar. */
  variant?: 'icon' | 'icon-muted' | 'utility'
}

const segmentBaseClassName =
  'relative z-1 inline-flex h-9 flex-1 items-center justify-center bg-clip-padding transition-all outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px [&_svg]:pointer-events-none [&_svg]:shrink-0'

const headerTrackClassName = 'bg-white/6 shadow-none'
const headerActivePillClassName =
  'border border-white/15 bg-white/10 text-zinc-200 shadow-sm backdrop-blur-sm hover:bg-white/15 hover:text-white focus-visible:border-white/25'
const headerGhostClassName = 'border border-white/15'
const headerDividerClassName =
  'pointer-events-none absolute inset-y-0 left-1/2 z-2 w-px -translate-x-1/2 bg-white/15'

const utilityTrackClassName =
  'rounded-xl border border-white/20 bg-white/10 shadow-none backdrop-blur-none'
const utilityActivePillClassName =
  'border border-white/20 bg-white/15 text-cream shadow-none hover:text-white focus-visible:ring-white/25 dark:border-white/15 dark:bg-white/15 dark:text-white'
const utilityDividerClassName =
  'pointer-events-none absolute inset-y-0 left-1/2 z-2 w-px -translate-x-1/2 bg-white/20'
const utilityIconClassName = 'text-cream hover:text-white dark:text-white'

const pageActivePillClassName =
  'border border-ink/10 bg-paper/80 text-ink/80 shadow-sm backdrop-blur-sm hover:bg-paper hover:text-ink focus-visible:border-ring dark:border-white/15 dark:bg-white/10 dark:text-zinc-200 dark:hover:bg-white/15 dark:hover:text-white'
const pageDividerClassName =
  'pointer-events-none absolute inset-y-0 left-1/2 z-2 w-px -translate-x-1/2 bg-ink/10 dark:bg-white/15'

export function ThemeToggle({
  className,
  variant = 'icon-muted',
}: ThemeToggleProps) {
  const [theme, setTheme] = useState<MorningTheme>(() => readStoredTheme())

  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme])

  const isDark = theme === 'dark'
  const useHeaderChrome = variant === 'icon-muted'
  const useUtilityChrome = variant === 'utility'
  const useSlidingPill = useHeaderChrome || useUtilityChrome

  const selectTheme = (next: MorningTheme) => {
    if (next === theme) return
    setTheme(next)
    writeStoredTheme(next)
    applyThemeToDocument(next)
  }

  const trackClassName = useUtilityChrome
    ? utilityTrackClassName
    : useHeaderChrome
      ? headerTrackClassName
      : undefined
  const activePillClassName = useUtilityChrome
    ? utilityActivePillClassName
    : useHeaderChrome
      ? headerActivePillClassName
      : pageActivePillClassName
  const dividerClassName = useUtilityChrome
    ? utilityDividerClassName
    : useHeaderChrome
      ? headerDividerClassName
      : pageDividerClassName
  const iconClassName = useUtilityChrome
    ? utilityIconClassName
    : useHeaderChrome
      ? 'text-zinc-200'
      : 'text-foreground'

  return (
    <div
      className={cn(
        'relative inline-flex h-9 w-17 shrink-0 overflow-hidden rounded-xl',
        trackClassName,
        className,
      )}
      role="group"
      aria-label="Theme"
    >
      {useHeaderChrome ? (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 z-0 w-1/2 transition-[left] duration-200 ease-out',
            headerGhostClassName,
            isDark
              ? 'left-1/2 rounded-r-xl border-l-0'
              : 'left-0 rounded-l-xl border-r-0',
          )}
        />
      ) : null}
      {useSlidingPill ? (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 z-0 w-1/2 transition-[left] duration-200 ease-out',
            activePillClassName,
            isDark
              ? 'left-0 rounded-l-xl border-r-0'
              : 'left-1/2 rounded-r-xl border-l-0',
          )}
        />
      ) : null}
      <span aria-hidden className={dividerClassName} />
      <button
        type="button"
        onClick={() => selectTheme('dark')}
        aria-pressed={isDark}
        className={cn(
          segmentBaseClassName,
          !useSlidingPill && 'bg-transparent hover:bg-transparent',
          iconClassName,
        )}
        aria-label="Dark mode"
        title="Dark mode"
      >
        <Moon className="size-4.25" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => selectTheme('light')}
        aria-pressed={!isDark}
        className={cn(
          segmentBaseClassName,
          !useSlidingPill && 'bg-transparent hover:bg-transparent',
          iconClassName,
        )}
        aria-label="Light mode"
        title="Light mode"
      >
        <Sun className="size-4.25" strokeWidth={2} aria-hidden />
      </button>
    </div>
  )
}
